import { useCallback, useEffect } from "react";
import { Grid, Typography, useTheme, Theme } from "@mui/material";
import { ContentThumbnail } from "src/components/ContentManagement/ContentThumbnail";
import { FileUpload } from "src/components/FileUpload/FileUpload";
import { IContentForm } from "./ManageContentWindow";
import { IContent } from "@vmosolutions/oc-interfaces";

interface IFileDropProps {
  formProps: IContentForm;
  setContentFile: (file: any) => void; //set the file, to later fetch
  requiresFile?: boolean;
  maxFileSize?: number;
  fileUploadRef: any;
  children?: any;
}

const fileNumMessageStyle = (theme: Theme): any => ({
  textAlign: "center",
  color: theme.custom.forms.focusedColor,
  float: "none",
  fontSize: 12,
  opacity: 0.5,
  padding: "1px",
});

export const FileDrop = (props: IFileDropProps): JSX.Element => {
  const { formProps, setContentFile, requiresFile, maxFileSize, fileUploadRef } = props;
  const theme = useTheme();
  const content: IContent = formProps?.values;

  useEffect(() => {
    if (requiresFile && !formProps?.values?.fileName && !formProps?.errors?.fileName) {
      formProps.setFieldError("fileName", "File Required");
    } else if (!requiresFile && formProps?.errors?.fileName) {
      formProps.setFieldError("fileName", null);
    }
  }, [formProps, formProps?.errors, requiresFile]);

  /**
   * Returns JSX element of file and thumbnail -- If file attached return this instead of drop area
   */
  const fileAlreadyAttched = (content ? [content] : null)?.map((file: any, index: number) => {
    return (
      <div key={file?.fileName + index}>
        {content && (
          <ContentThumbnail
            size={120}
            contentId={content._id}
            fileType={content.fileType}
            fileName={content.fileName}
          />
        )}
        {content?.fileName}{" "}
      </div>
    );
  });

  const handleOnFile = (newFile: any, fileContents: ArrayBuffer): void => {
    setContentFile({ file: newFile, fileData: fileContents });
    formProps.setFieldValue("fileName", newFile?.name);
    formProps.setFieldTouched("fileName", true);
    if (requiresFile) formProps.setFieldError("fileName", null);

    formProps.setFieldValue("fileSize", newFile?.size);
    formProps.setFieldTouched("fileSize", true);
    //set max file size error
    if (maxFileSize && newFile?.size > maxFileSize) {
      formProps.setFieldError("fileSize", `Exceeds Max File Size ${maxFileSize}`);
    }
  };

  const handleRemoveFile = useCallback((): void => {
    setContentFile(null);
    formProps.setFieldValue("fileName", undefined);
    formProps.setFieldTouched("fileName", true);
    formProps.setFieldValue("fileSize", undefined);
    formProps.setFieldTouched("fileSize", true);
    //if there is a max file size error remove it
    if (formProps.getFieldMeta("fileSize")?.error) formProps.setFieldError("fileSize", null);
  }, [formProps, setContentFile]);

  return (
    <Grid item>
      <Typography sx={{ textAlign: "center" }}>Attachment </Typography>
      <Typography sx={fileNumMessageStyle(theme)}>
        File(s) Attached {content?.fileName || content?.fileSize ? 1 : 0}
        /1
      </Typography>
      {content?._id && fileAlreadyAttched}
      <Grid item style={{ display: content?._id ? "none" : "block" }}>
        <FileUpload
          onFile={handleOnFile}
          handleRemoveFile={content.fileName && content?.fileSize ? null : handleRemoveFile}
          data-testid="content-attachment-input"
          ref={fileUploadRef}
        />
      </Grid>
    </Grid>
  );
};
